'use client';

import React, { useState, useCallback } from 'react';
import { kavrisNodes, KavrisNode } from '@/data/kavrisData';
import StatusBadge from '@/components/ui/StatusBadge';
import AuthorityBadge from '@/components/ui/AuthorityBadge';
import { X, ChevronRight, Map, BookOpen, Layers, Building2, ShieldCheck, Database, Compass } from 'lucide-react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


const nodeIcons: Record<string, React.ElementType> = {
  'node-kavris-core': Compass,
  'node-codex': BookOpen,
  'node-atlas': Map,
  'node-product-framework': Layers,
  'node-foundation': ShieldCheck,
  'node-pf001': Building2,
  'node-knowledge': Database,
};

// Node positions as percentage of container (used for SVG lines)
const nodePositions: Record<string, { cx: number; cy: number }> = {
  'node-kavris-core': { cx: 50, cy: 50 },
  'node-codex':        { cx: 20, cy: 22 },
  'node-atlas':        { cx: 80, cy: 22 },
  'node-product-framework': { cx: 82, cy: 72 },
  'node-foundation':   { cx: 50, cy: 10 },
  'node-pf001':        { cx: 18, cy: 72 },
  'node-knowledge':    { cx: 50, cy: 88 },
};

const nodeHrefs: Record<string, string> = {
  'node-codex': '/codex',
  'node-atlas': '/atlas',
  'node-product-framework': '/product-framework',
  'node-pf001': '/pf-001-enterprise',
};

interface BreadcrumbItem {
  id: string;
  label: string;
}

export default function NavigatorCanvas() {
  const [selectedNode, setSelectedNode] = useState<KavrisNode | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([
    { id: 'node-kavris-core', label: 'KAVRIS' },
  ]);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  const handleNodeClick = useCallback((node: KavrisNode) => {
    setSelectedNode(node);
    if (node.id !== 'node-kavris-core') {
      setBreadcrumb([
        { id: 'node-kavris-core', label: 'KAVRIS' },
        { id: node.id, label: node.label },
      ]);
    } else {
      setBreadcrumb([{ id: 'node-kavris-core', label: 'KAVRIS' }]);
    }
  }, []);

  const handleBreadcrumbClick = useCallback((item: BreadcrumbItem) => {
    const node = kavrisNodes.find(n => n.id === item.id);
    if (node) {
      setSelectedNode(node.id === 'node-kavris-core' ? null : node);
      setBreadcrumb(prev => {
        const idx = prev.findIndex(b => b.id === item.id);
        return prev.slice(0, idx + 1);
      });
    }
  }, []);

  const isConnectedToSelected = (nodeId: string) => {
    if (!selectedNode) return false;
    return selectedNode.relatedIds.includes(nodeId) || selectedNode.id === nodeId;
  };

  const relatedNodes = selectedNode
    ? kavrisNodes.filter(n => selectedNode.relatedIds.includes(n.id))
    : [];

  return (
    <div className="flex h-full" style={{ minHeight: 'calc(100vh - 56px)' }}>
      {/* Canvas area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative kavris-grid-bg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-background/60 backdrop-blur-sm">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1 text-xs text-muted-foreground font-mono-data">
              {breadcrumb.map((item, i) => (
                <React.Fragment key={`bc-${item.id}`}>
                  {i > 0 && <ChevronRight size={10} className="text-muted-foreground/50" />}
                  <button
                    onClick={() => handleBreadcrumbClick(item)}
                    className={`hover:text-foreground transition-colors ${
                      i === breadcrumb.length - 1 ? 'text-foreground font-medium' : 'text-muted-foreground'
                    }`}
                  >
                    {item.label}
                  </button>
                </React.Fragment>
              ))}
            </div>
            <h1 className="text-lg font-semibold text-foreground">Navigator</h1>
          </div>
          <div className="text-xs text-muted-foreground font-mono-data hidden sm:block">
            {kavrisNodes.length} nodes · {kavrisNodes.filter(n => n.id !== 'node-kavris-core').length} connections
          </div>
        </div>

        {/* Node graph */}
        <div className="flex-1 relative overflow-hidden">
          {/* SVG connection lines */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            preserveAspectRatio="none"
          >
            <defs>
              <marker id="arrowhead" markerWidth="6" markerHeight="4" refX="6" refY="2" orient="auto">
                <polygon points="0 0, 6 2, 0 4" fill="rgba(79,142,247,0.3)" />
              </marker>
              <marker id="arrowhead-active" markerWidth="6" markerHeight="4" refX="6" refY="2" orient="auto">
                <polygon points="0 0, 6 2, 0 4" fill="rgba(79,142,247,0.8)" />
              </marker>
            </defs>
            {kavrisNodes
              .filter(n => n.id !== 'node-kavris-core')
              .map(node => {
                const from = nodePositions['node-kavris-core'];
                const to = nodePositions[node.id];
                if (!from || !to) return null;
                const isActive = selectedNode?.id === node.id || selectedNode?.id === 'node-kavris-core';
                const isHovered = hoveredNode === node.id;
                return (
                  <line
                    key={`line-${node.id}`}
                    x1={`${from.cx}%`} y1={`${from.cy}%`}
                    x2={`${to.cx}%`} y2={`${to.cy}%`}
                    stroke={isActive || isHovered ? 'rgba(79,142,247,0.6)' : 'rgba(79,142,247,0.15)'}
                    strokeWidth={isActive || isHovered ? 1.5 : 1}
                    strokeDasharray={isActive ? 'none' : '4 4'}
                    markerEnd={isActive || isHovered ? 'url(#arrowhead-active)' : 'url(#arrowhead)'}
                    className="transition-all duration-300"
                  />
                );
              })}
            {/* PF → PF-001 connection */}
            {(() => {
              const from = nodePositions['node-product-framework'];
              const to = nodePositions['node-pf001'];
              if (!from || !to) return null;
              return (
                <line
                  key="line-pf-pf001"
                  x1={`${from.cx}%`} y1={`${from.cy}%`}
                  x2={`${to.cx}%`} y2={`${to.cy}%`}
                  stroke="rgba(245,158,11,0.2)"
                  strokeWidth="1"
                  strokeDasharray="3 3"
                />
              );
            })()}
          </svg>

          {/* Nodes */}
          {kavrisNodes.map(node => {
            const pos = nodePositions[node.id];
            if (!pos) return null;
            const Icon = nodeIcons[node.id] || Compass;
            const isCore = node.id === 'node-kavris-core';
            const isSelected = selectedNode?.id === node.id;
            const isRelated = isConnectedToSelected(node.id);
            const isHov = hoveredNode === node.id;

            return (
              <button
                key={node.id}
                onClick={() => handleNodeClick(node)}
                onMouseEnter={() => setHoveredNode(node.id)}
                onMouseLeave={() => setHoveredNode(null)}
                className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/60 rounded-xl ${
                  isCore ? 'z-10' : 'z-5'
                }`}
                style={{ left: `${pos.cx}%`, top: `${pos.cy}%` }}
                aria-label={`Navigate to ${node.label}`}
              >
                <div
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all duration-200 ${
                    isCore
                      ? 'bg-primary/10 border-primary/40 node-glow-active min-w-[100px]'
                      : isSelected
                      ? 'bg-primary/10 border-primary/50 node-glow-active'
                      : isRelated && selectedNode
                      ? 'bg-muted/60 border-primary/25 node-glow'
                      : isHov
                      ? 'bg-muted/60 border-border/80 node-glow' :'bg-card/80 border-border hover:border-primary/20'
                  } backdrop-blur-sm`}
                >
                  <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${
                    isCore ? 'bg-primary/20' : 'bg-muted/50'
                  }`}>
                    <Icon size={isCore ? 18 : 14} className={isCore || isSelected ? 'text-primary' : 'text-muted-foreground'} />
                  </div>
                  <span className={`text-xs font-semibold whitespace-nowrap ${
                    isCore || isSelected ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {node.label}
                  </span>
                  <div className="flex items-center gap-1">
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      node.status === 'Working' ? 'bg-primary' :
                      node.status === 'Draft' ? 'bg-[#f59e0b]' :
                      node.status === 'Experimental' ? 'bg-[#a855f7]' :
                      node.status === 'Approved' ? 'bg-[#22c55e]' :
                      'bg-muted-foreground'
                    }`} />
                    <span className="text-[9px] font-mono-data text-muted-foreground">{node.status}</span>
                  </div>
                </div>
              </button>
            );
          })}

          {/* Instruction overlay when nothing selected */}
          {!selectedNode && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center pointer-events-none">
              <p className="text-xs text-muted-foreground font-mono-data">
                Click any node to explore its context and relationships
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Context panel */}
      {selectedNode && (
        <div className="w-80 xl:w-96 border-l border-border bg-card flex-shrink-0 overflow-y-auto panel-slide-in">
          <div className="p-5">
            {/* Panel header */}
            <div className="flex items-start justify-between gap-3 mb-5">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground">
                    {selectedNode.type}
                  </span>
                </div>
                <h2 className="text-lg font-semibold text-foreground">{selectedNode.label}</h2>
              </div>
              <button
                onClick={() => { setSelectedNode(null); setBreadcrumb([{ id: 'node-kavris-core', label: 'KAVRIS' }]); }}
                className="w-7 h-7 flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors flex-shrink-0"
              >
                <X size={14} />
              </button>
            </div>

            {/* Status row */}
            <div className="flex items-center gap-2 mb-5">
              <StatusBadge status={selectedNode.status} size="sm" />
              <AuthorityBadge authority={selectedNode.authority} size="sm" />
              <span className="text-[10px] font-mono-data text-muted-foreground ml-auto">{selectedNode.version}</span>
            </div>

            {/* Description */}
            <div className="mb-5">
              <p className="text-sm text-muted-foreground leading-relaxed">{selectedNode.description}</p>
            </div>

            {/* Layer */}
            <div className="mb-5 p-3 rounded border border-border bg-muted/20">
              <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground mb-1">Layer</div>
              <div className="text-sm font-medium text-foreground">{selectedNode.layer}</div>
            </div>

            {/* Parent */}
            {selectedNode.parentId && (
              <div className="mb-5">
                <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground mb-2">Parent Node</div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="text-primary/50">↑</span>
                  <span>{kavrisNodes.find(n => n.id === selectedNode.parentId)?.label || selectedNode.parentId}</span>
                </div>
              </div>
            )}

            {/* Related nodes */}
            {relatedNodes.length > 0 && (
              <div className="mb-5">
                <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground mb-3">
                  Related Nodes ({relatedNodes.length})
                </div>
                <div className="flex flex-col gap-2">
                  {relatedNodes.map(related => (
                    <button
                      key={`related-${related.id}`}
                      onClick={() => handleNodeClick(related)}
                      className="flex items-center gap-2 p-2 rounded border border-border hover:border-primary/30 hover:bg-primary/5 transition-all text-left"
                    >
                      <span className="text-primary/50 text-xs">↔</span>
                      <span className="text-sm text-foreground flex-1">{related.label}</span>
                      <StatusBadge status={related.status} size="xs" showDot />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Navigate link */}
            {nodeHrefs[selectedNode.id] && (
              <div className="mt-4 pt-4 border-t border-border">
                <Link
                  href={nodeHrefs[selectedNode.id]}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded border border-primary/30 bg-primary/5 text-primary text-sm font-medium hover:bg-primary/10 transition-colors active:scale-95"
                >
                  Open {selectedNode.label}
                  <ChevronRight size={14} />
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}